export const whatWeDoIconTokens = [
    'arrowRight',
    'award',
    'badgeCheck',
    'barChart',
    'check',
    'circleCheck',
    'dollar',
    'globe',
    'layout',
    'megaphone',
    'monitor',
    'rocket',
    'search',
    'shieldCheck',
    'sparkles',
    'target',
    'trendingUp',
    'users',
    'zap',
] as const;

export type WhatWeDoIconToken = (typeof whatWeDoIconTokens)[number];

export const whatWeDoTokens = {
    colors: {
        light: {
            background: '#ffffff',
            surface: '#ffffff',
            text: '#0b1220',
            textSoft: '#344054',
            textMuted: '#667085',
            border: 'rgba(15, 23, 42, 0.1)',
            badgeSurface: 'rgba(255, 255, 255, 0.94)',
            imageFallback: '#eef3f0',
            dot: 'rgba(15, 23, 42, 0.14)',
            shadow: '0 20px 54px rgba(15, 23, 42, 0.12)',
            badgeShadow: '0 14px 34px rgba(15, 23, 42, 0.14)',
        },
        dark: {
            background: '#08110d',
            surface: '#101a15',
            text: '#f7fbf8',
            textSoft: '#d1ddd5',
            textMuted: '#9fafaa',
            border: 'rgba(255, 255, 255, 0.14)',
            badgeSurface: 'rgba(16, 26, 21, 0.92)',
            imageFallback: '#17231d',
            dot: 'rgba(255, 255, 255, 0.18)',
            shadow: '0 24px 60px rgba(0, 0, 0, 0.34)',
            badgeShadow: '0 18px 38px rgba(0, 0, 0, 0.36)',
        },
        accent: '#22b428',
        accentDark: '#178a1d',
        accentSoft: 'rgba(34, 180, 40, 0.13)',
        accentContrast: '#ffffff',
    },
    radius: {
        image: 8,
        badge: 8,
        button: 8,
        icon: 999,
    },
    spacing: {
        sectionY: 56,
        sectionX: 24,
        desktopGap: 64,
        tabletGap: 38,
        pointsGap: 18,
        mobileSectionY: 36,
        mobileSectionX: 16,
    },
    sizes: {
        maxWidth: 1440,
        contentMaxWidth: 530,
        mediaMinHeight: 420,
        mobileMediaMinHeight: 260,
        pointIcon: 28,
        badgeIcon: 56,
        ctaMinHeight: 48,
    },
    typography: {
        fontFamily:
            'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        eyebrow: {
            fontSize: 13,
            lineHeight: 1.15,
            fontWeight: 850,
            letterSpacing: 0,
            textTransform: 'uppercase' as const,
        },
        title: {
            fontSize: 38,
            tabletFontSize: 34,
            mobileFontSize: 28,
            lineHeight: 1.12,
            fontWeight: 850,
            letterSpacing: 0,
        },
        description: {
            fontSize: 15,
            lineHeight: 1.6,
            fontWeight: 500,
            letterSpacing: 0,
        },
        point: {
            fontSize: 16,
            mobileFontSize: 15,
            lineHeight: 1.42,
            fontWeight: 650,
            letterSpacing: 0,
        },
        cta: {
            fontSize: 15,
            lineHeight: 1,
            fontWeight: 800,
            letterSpacing: 0,
        },
        badgeTitle: {
            fontSize: 14,
            lineHeight: 1.3,
            fontWeight: 650,
            letterSpacing: 0,
        },
        badgeValue: {
            fontSize: 28,
            mobileFontSize: 23,
            lineHeight: 1.05,
            fontWeight: 850,
            letterSpacing: 0,
        },
    },
    defaults: {
        eyebrow: 'What we do',
        title: 'Digital Solutions That Deliver Results',
        description: '',
        pointIcon: 'circleCheck' as WhatWeDoIconToken,
        ctaIcon: 'arrowRight' as WhatWeDoIconToken,
        points: [
            'Website Design & Development',
            'Google Ads & Social Media Ads',
            'SEO & Local Search',
            'Lead Generation & Automation',
            'Analytics & Conversion Tracking',
        ],
        cta: {
            label: 'Explore All Services',
            href: '#services',
        },
        badges: [
            {
                title: 'Projects Delivered',
                value: '250+',
                icon: 'rocket' as WhatWeDoIconToken,
            },
            {
                title: 'Client Revenue Growth',
                value: '300%+',
                icon: 'dollar' as WhatWeDoIconToken,
            },
        ],
    },
} as const;

export type WhatWeDoColorToken = keyof typeof whatWeDoTokens.colors.light;
